import * as THREE from "three";
import { viewportSize } from '../utils/ui.js';
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { SHOW_DEV_TOOLS } from "../config.js";
import { FRAME_VERSION, migrateCamera, needsFrameMigration } from '../utils/orientationMigration.js';
import { RoomCutaway } from "./RoomCutaway.js";

// Constants for camera configuration
const CAMERA_FOV = 35;
const CAMERA_NEAR = 0.1;
const CAMERA_FAR = 1000;

// Orthographic camera settings
const ORTHO_FRUSTUM_SIZE = 5;

// Default positions
const DEFAULT_2D_POSITION = new THREE.Vector3(0, 10, 0);
const DEFAULT_2D_TARGET = new THREE.Vector3(0, 0, 0);
const DEFAULT_3D_POSITION = new THREE.Vector3(0, 1.6, 4);
// Embeds start further out so the whole preset reads as a product shot, not a close-up.
const DEFAULT_EMBED_3D_POSITION = new THREE.Vector3(0, 1.6, 8);
// Single-element embeds (?element=) frame closer and look slightly up at the seat.
const DEFAULT_EMBED_ELEMENT_POSITION = new THREE.Vector3(2.4, 1.3, 2.6);
const DEFAULT_EMBED_ELEMENT_TARGET = new THREE.Vector3(0, 0.35, 0);
const DEFAULT_3D_TARGET = new THREE.Vector3(0, 0, 0);

// Controls configuration
const DAMPING_FACTOR_2D = 0.1;
const DAMPING_FACTOR_3D = 0.08;
const KEY_PAN_SPEED = 20.0;
const MAX_TARGET_RADIUS = 5;
// Per-frame step for the glide-back-to-center animation when 3D panning is switched
// off. ~0.06/frame ≈ a 0.5s ease at 60fps (frame-based to match the damping style).
const RECENTER_STEP = 0.06;

// 3D mode limits
const MIN_DISTANCE_3D = 1;
// Walled rooms share the studio's reach; RoomCutaway opens a wall the camera passes.
const MAX_DISTANCE_3D = 16;
const MAX_POLAR_ANGLE = Math.PI / 2.02;
// Ceiling for the panned polar limit — past this the view reads as looking up from
// under the furniture even when the geometry would still allow it.
const MAX_POLAR_ANGLE_PANNED = Math.PI / 1.7;
// Per-frame ease used when the polar limit shrinks. OrbitControls clamps phi instantly,
// so collapsing the limit in one frame would snap the view upward.
const POLAR_RELAX_STEP = 0.02;

// Floor clamp for a walled room; updatePolarLimit solves for the same height.
const CAMERA_MIN_ORBIT_Y = 0.5;

// LocalStorage key
const CAMERA_STATE_KEY = 'threejs_camera_state';

export class CameraManager {
  constructor(renderer, app = null) {
    this.renderer = renderer;
    this.app = app;
    this.roomCutaway = new RoomCutaway(app, renderer);

    this.perspectiveCamera = null;
    this.orthographicCamera = null;
    this.camera = null;
    this.controls = null;
    this.logCameraPosition = false;

    this.sceneMode = null;
    this.isTransitioning = false;

    // 3D orbit panning is off by default (matches the top-down-editor workflow);
    // toggled via Settings → Panning (3D). 2D always pans regardless.
    this.panningEnabled = false;

    // Active glide-back-to-center animation (set when panning is switched off), or null.
    this._recenter = null;

    // Current 3D polar limit; widens with the panned target height, eases back down.
    this._maxPolarAngle = MAX_POLAR_ANGLE;

    this.init();
  }

  init() {
    const savedState = this.getSavedState();
    // Embeds are always interactive 3D (rotate + zoom), never the 2D top-down editor.
    this.sceneMode = this.app?.isEmbedMode ? "3D" : (savedState?.sceneMode || "3D");

    this.createCameras();
    this.createControls();
  }

  // Resolve embed framing (position/target) from a `{ position?, target? }` config,
  // falling back per-field to the environment-level embed camera, then to the hardcoded
  // defaults. Precedence: per-preset embedCamera → environments.json settings.embed →
  // DEFAULT_EMBED_* constants. `cfg` is the highest-priority layer (may be null).
  _resolveEmbedCamera(cfg = null) {
    const embed = this.app?.embedSettings || {};
    const isElement = !!this.app?.embedElement;
    const envCfg = (isElement ? embed.elementCamera : embed.camera) || {};
    const pos = cfg?.position ?? envCfg.position;
    const target = cfg?.target ?? envCfg.target;
    // FOV precedence mirrors position/target: per-preset → settings.embed → CAMERA_FOV.
    // A lower FOV (telephoto) reduces perspective convergence so the preset's vertical
    // edges read as upright rather than fanning out toward the frame corners.
    const fov = cfg?.fov ?? envCfg.fov ?? CAMERA_FOV;
    // focalLength (mm, 35mm-equivalent) wins over fov when set.
    const focalLength = cfg?.focalLength ?? envCfg.focalLength ?? null;
    return {
      position: Array.isArray(pos)
        ? new THREE.Vector3(...pos)
        : (isElement ? DEFAULT_EMBED_ELEMENT_POSITION.clone() : DEFAULT_EMBED_3D_POSITION.clone()),
      target: Array.isArray(target)
        ? new THREE.Vector3(...target)
        : (isElement ? DEFAULT_EMBED_ELEMENT_TARGET.clone() : DEFAULT_3D_TARGET.clone()),
      fov,
      focalLength,
    };
  }

  _applyPerspectiveFov(fov, focalLength) {
    if (focalLength != null) {
      this.perspectiveCamera.setFocalLength(focalLength);
    } else {
      this.perspectiveCamera.fov = fov;
      this.perspectiveCamera.updateProjectionMatrix();
    }
  }

  // Environment-level embed framing (no per-preset override). Used at camera creation,
  // before the preset is known.
  _embedCameraConfig() {
    return this._resolveEmbedCamera(null);
  }

  // Re-aim the 3D camera onto a (possibly per-preset) embed config, overriding the
  // environment-level framing applied at construction. Called after the preset loads
  // (main.js embed block) once the layout's optional `embedCamera` is known.
  applyEmbedCamera(cfg) {
    this.applyCameraPose(this._resolveEmbedCamera(cfg));
  }

  /** Aim at an explicit pose; callers resolve their own precedence first. */
  applyCameraPose({ position, target, fov = null, focalLength = null }) {
    this.perspectiveCamera.position.copy(position);
    if (fov !== null || focalLength !== null) {
      this._applyPerspectiveFov(fov ?? this.perspectiveCamera.fov, focalLength);
    }
    this.perspectiveCamera.lookAt(target);
    if (this.controls) {
      this.controls.target.copy(target);
      this.controls.update();
    }
  }

  /** Current 3D pose, shaped for applyCameraPose() — how a temporary re-aim is undone. */
  getCameraPose() {
    return {
      position: this.perspectiveCamera.position.clone(),
      target: this.controls
        ? this.controls.target.clone()
        : new THREE.Vector3().copy(this.perspectiveCamera.position)
          .add(this.perspectiveCamera.getWorldDirection(new THREE.Vector3())),
      fov: this.perspectiveCamera.fov,
    };
  }

  createCameras() {
    const { width, height } = viewportSize();
    const aspect = width / height;

    // Perspective camera for 3D mode
    this.perspectiveCamera = new THREE.PerspectiveCamera(
      CAMERA_FOV, aspect, CAMERA_NEAR, CAMERA_FAR
    );
    this.perspectiveCamera.up.set(0, 1, 0);
    const embedCam = this.app?.isEmbedMode ? this._embedCameraConfig() : null;
    this.perspectiveCamera.position.copy(embedCam ? embedCam.position : DEFAULT_3D_POSITION);
    if (embedCam) {
      this._applyPerspectiveFov(embedCam.fov, embedCam.focalLength);
    }
    this.perspectiveCamera.lookAt(embedCam ? embedCam.target : DEFAULT_3D_TARGET);

    // Orthographic camera for 2D mode
    const frustumWidth = ORTHO_FRUSTUM_SIZE * aspect;
    this.orthographicCamera = new THREE.OrthographicCamera(
      -frustumWidth / 2, frustumWidth / 2,
      ORTHO_FRUSTUM_SIZE / 2, -ORTHO_FRUSTUM_SIZE / 2,
      CAMERA_NEAR, CAMERA_FAR
    );
    // Set up vector so 2D view orientation matches the 3D camera view
    this.orthographicCamera.up.set(0, 0, -1);
    this.orthographicCamera.position.copy(DEFAULT_2D_POSITION);
    this.orthographicCamera.lookAt(DEFAULT_2D_TARGET);

    // Set active camera
    this.camera = this.sceneMode === "2D" ? this.orthographicCamera : this.perspectiveCamera;
  }

  createControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;

    if (this.sceneMode === "2D") {
      this.controls.target.copy(DEFAULT_2D_TARGET);
      this.configure2DControls();
    } else {
      // Embeds look at their product-shot target; the editor uses the scene origin.
      this.controls.target.copy(
        this.app?.isEmbedMode ? this._embedCameraConfig().target : DEFAULT_3D_TARGET
      );
      this.configure3DControls();
    }

    this.loadCameraState();
    this.setupControlsChangeListener();
  }

  setupControlsChangeListener() {
    let saveTimeout = null;
    this.controls.addEventListener('change', () => {
      if (saveTimeout) clearTimeout(saveTimeout);
      saveTimeout = setTimeout(() => this.saveCameraState(), 500);
      if (SHOW_DEV_TOOLS && this.logCameraPosition) this._logCameraPosition();
    });
  }

  // Dev-only: print the active camera position / target / fov on each control change.
  // Toggled from the Settings panel (only shown when SHOW_DEV_TOOLS).
  _logCameraPosition() {
    const p = this.camera.position;
    const t = this.controls.target;
    const r = (n) => Math.round(n * 100) / 100;
    console.log(
      `[camera] position: [${r(p.x)}, ${r(p.y)}, ${r(p.z)}]`,
      `target: [${r(t.x)}, ${r(t.y)}, ${r(t.z)}]`,
      this.camera.isPerspectiveCamera ? `fov: ${this.camera.fov}` : `zoom: ${r(this.camera.zoom)}`
    );
  }

  /** Enable/disable panning in 3D orbit mode. Applies live when already in 3D. */
  set3DPanningEnabled(enabled) {
    this.panningEnabled = enabled;
    if (this.sceneMode === "3D" && this.controls) {
      this.controls.enablePan = enabled;
      // Give the right mouse button a pan action while left keeps rotating.
      this.controls.mouseButtons.RIGHT = enabled ? THREE.MOUSE.PAN : null;
      // Turning panning off glides the view back so any pan offset is undone;
      // turning it back on cancels a glide that's still in flight.
      if (enabled) {
        this._recenter = null;
      } else {
        this.recenter3DTarget();
      }
      this.controls.update();
    }
  }

  /**
   * Start a smooth glide that returns controls.target to the 3D origin, moving the
   * camera by the same offset so the orbit distance/angle is preserved — i.e. it
   * undoes a screen-space pan rather than swinging the camera around. The animation
   * is advanced each frame from update(); a no-op when already centered.
   */
  recenter3DTarget() {
    if (this.sceneMode !== "3D" || !this.controls) return;
    const offset = DEFAULT_3D_TARGET.clone().sub(this.controls.target);
    if (offset.lengthSq() < 1e-6) {
      this._recenter = null;
      return;
    }
    this._recenter = {
      camFrom: this.camera.position.clone(),
      camTo: this.camera.position.clone().add(offset),
      tgtFrom: this.controls.target.clone(),
      tgtTo: DEFAULT_3D_TARGET.clone(),
      t: 0,
    };
  }

  // Advance the recenter glide by one frame. Both camera and target move by the same
  // interpolated offset, so the spherical (radius/angles) OrbitControls tracks is
  // unchanged and damping doesn't fight it.
  tickRecenter() {
    const r = this._recenter;
    if (!r) return;
    // Abandon the glide if we've left 3D (controls/camera get swapped on a mode switch).
    if (this.sceneMode !== "3D") {
      this._recenter = null;
      return;
    }
    r.t = Math.min(1, r.t + RECENTER_STEP);
    const e = 1 - Math.pow(1 - r.t, 3); // easeOutCubic
    this.camera.position.lerpVectors(r.camFrom, r.camTo, e);
    this.controls.target.lerpVectors(r.tgtFrom, r.tgtTo, e);
    if (r.t >= 1) this._recenter = null;
  }

  get3DPanningEnabled() {
    return this.panningEnabled;
  }

  /**
   * Polar limit the current orbit would allow: panning up lifts controls.target off the
   * floor, so the camera can swing further past horizontal before it drops to
   * CAMERA_MIN_ORBIT_Y. Solves camera.y = target.y + radius·cos(phi) for that height.
   * Never tighter than the default limit (panning down must not restrict the orbit).
   */
  _desiredMaxPolarAngle() {
    if (!this.panningEnabled) return MAX_POLAR_ANGLE;
    const radius = this.camera.position.distanceTo(this.controls.target);
    if (radius < 1e-4) return MAX_POLAR_ANGLE;
    const cos = THREE.MathUtils.clamp((CAMERA_MIN_ORBIT_Y - this.controls.target.y) / radius, -1, 1);
    return THREE.MathUtils.clamp(Math.acos(cos), MAX_POLAR_ANGLE, MAX_POLAR_ANGLE_PANNED);
  }

  // Track the desired limit: widen immediately (the camera is already inside it, so
  // there is nothing to snap), ease back down a step at a time.
  updatePolarLimit() {
    if (this.sceneMode !== "3D" || !this.controls) return;
    const desired = this._desiredMaxPolarAngle();
    this._maxPolarAngle = desired >= this._maxPolarAngle
      ? desired
      : Math.max(desired, this._maxPolarAngle - POLAR_RELAX_STEP);
    this.controls.maxPolarAngle = this._maxPolarAngle;
  }

  // ===== MODE SWITCHING =====
  toggleSceneMode(onTransitionStart, onTransitionComplete) {
    if (this.isTransitioning) return;
    const newMode = this.sceneMode === "3D" ? "2D" : "3D";
    this.setSceneMode(newMode, onTransitionStart, onTransitionComplete);
  }

  setSceneMode(mode, onTransitionStart, onTransitionComplete) {
    if (this.isTransitioning || this.sceneMode === mode) return;

    this.isTransitioning = true;

    if (onTransitionStart) onTransitionStart();

    const newCamera = mode === "2D" ? this.orthographicCamera : this.perspectiveCamera;
    const newTarget = mode === "2D" ? DEFAULT_2D_TARGET.clone() : DEFAULT_3D_TARGET.clone();

    // Switch camera (instant - perspective and orthographic can't be smoothly interpolated)
    this.sceneMode = mode;
    this.camera = newCamera;

    // Reset 2D camera to ensure perfect top-down view
    if (mode === '2D') {
      this.camera.position.copy(DEFAULT_2D_POSITION);
      this.camera.up.set(0, 0, -1);
    }

    this.camera.lookAt(newTarget);
    this.camera.updateMatrixWorld();

    // Recreate controls for new camera
    this.controls.dispose();
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.target.copy(newTarget);

    if (mode === "2D") {
      this.configure2DControls();
    } else {
      this.configure3DControls();
    }

    this.setupControlsChangeListener();
    this.isTransitioning = false;
    this.saveCameraState();

    if (onTransitionComplete) onTransitionComplete(mode);
  }

  // ===== CONTROLS CONFIGURATION =====
  configure2DControls() {
    this.controls.enableRotate = false;
    this.controls.enablePan = true;
    this.controls.enableZoom = true;

    this.controls.minZoom = 0.5;
    this.controls.maxZoom = 4;

    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: null,
    };

    this.controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };

    this.controls.screenSpacePanning = true;
    this.controls.keyPanSpeed = KEY_PAN_SPEED;
    this.controls.maxTargetRadius = MAX_TARGET_RADIUS;
    this.controls.dampingFactor = DAMPING_FACTOR_2D;

    this.controls.update();
  }

  configure3DControls() {
    this.controls.enableRotate = true;
    this.controls.enablePan = this.panningEnabled;
    this.controls.enableZoom = true;

    this.controls.minDistance = MIN_DISTANCE_3D;
    this.controls.maxDistance = MAX_DISTANCE_3D;
    this.controls.minPolarAngle = 0;
    // Target is reset to the origin below, so the dynamic limit starts at the default.
    this._maxPolarAngle = MAX_POLAR_ANGLE;
    this.controls.maxPolarAngle = MAX_POLAR_ANGLE;

    this.controls.target.copy(DEFAULT_3D_TARGET);

    this.controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: this.panningEnabled ? THREE.MOUSE.PAN : null,
    };

    this.controls.dampingFactor = DAMPING_FACTOR_3D;
    this.controls.update();
  }

  // ===== EVENT HANDLING =====
  onWindowResize(size = viewportSize()) {
    const aspect = size.width / size.height;

    this.perspectiveCamera.aspect = aspect;
    this.perspectiveCamera.updateProjectionMatrix();

    const frustumWidth = ORTHO_FRUSTUM_SIZE * aspect;
    this.orthographicCamera.left = -frustumWidth / 2;
    this.orthographicCamera.right = frustumWidth / 2;
    this.orthographicCamera.top = ORTHO_FRUSTUM_SIZE / 2;
    this.orthographicCamera.bottom = -ORTHO_FRUSTUM_SIZE / 2;
    this.orthographicCamera.updateProjectionMatrix();
  }

  update() {
    this.tickRecenter();
    this.updatePolarLimit();
    this.controls.update();
    this.enforceCameraBounds();
    this.roomCutaway.update(this.camera);
  }

  isWhiteRoom() {
    return this.app?.getSceneManager()?.getEnvironmentManager()?.isWhiteRoom() === true;
  }

  enforceCameraBounds() {
    if (this.sceneMode !== "3D") return;

    const pos = this.perspectiveCamera.position;

    // Walls the camera passes are cut away (RoomCutaway), so only the floor bounds it.
    if (!this.isWhiteRoom()) pos.y = Math.max(pos.y, CAMERA_MIN_ORBIT_Y);

    this.enforceFurnitureClearance(pos);
  }

  // Push the camera out of furniture it would clip into. Skipped while at rest, so
  // the furniture sweep only runs when the view is moving.
  enforceFurnitureClearance(pos) {
    const collisionSystem = this.app?.getCollisionSystem?.();
    if (!collisionSystem) return;

    if (this._lastClearancePos &&
        this._lastClearancePos.distanceToSquared(pos) < 1e-8) {
      return;
    }

    collisionSystem.resolveCameraClearance(pos);

    if (!this._lastClearancePos) this._lastClearancePos = new THREE.Vector3();
    this._lastClearancePos.copy(pos);
  }

  // ===== PUBLIC API =====
  getCamera() {
    return this.camera;
  }

  getPerspectiveCamera() {
    return this.perspectiveCamera;
  }

  getOrthographicCamera() {
    return this.orthographicCamera;
  }

  getControls() {
    return this.controls;
  }

  getSceneMode() {
    return this.sceneMode;
  }

  isInTransition() {
    return this.isTransitioning;
  }

  onEnvironmentChange() {
    if (this.sceneMode === "3D") {
      this.configure3DControls();
    }
  }

  // ===== STATE PERSISTENCE =====
  getSavedState() {
    try {
      const saved = localStorage.getItem(CAMERA_STATE_KEY);
      if (!saved) return null;
      const state = JSON.parse(saved);
      if (!state.position || !state.target || !state.sceneMode) return null;
      // Saved before the +Z-front frame: same view, turned with the world.
      return needsFrameMigration(state.version) ? migrateCamera(state) : state;
    } catch (e) {
      console.warn('Failed to get saved camera state:', e);
      return null;
    }
  }

  getCameraState() {
    const activeCamera = this.sceneMode === "2D" ? this.orthographicCamera : this.perspectiveCamera;
    return {
      position: { x: activeCamera.position.x, y: activeCamera.position.y, z: activeCamera.position.z },
      target: { x: this.controls.target.x, y: this.controls.target.y, z: this.controls.target.z },
      sceneMode: this.sceneMode
    };
  }

  setCameraState(state) {
    try {
      // Embeds keep the default 3D product-shot framing — ignore a preset's saved camera
      // (which may be a 2D top-down view) so the embed always stays rotatable 3D.
      if (this.app?.isEmbedMode) return false;
      if (!state?.position || !state?.target) return false;

      if (state.sceneMode && state.sceneMode !== this.sceneMode) {
        this.sceneMode = state.sceneMode;
        this.camera = state.sceneMode === '2D' ? this.orthographicCamera : this.perspectiveCamera;

        this.controls.dispose();
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;

        if (this.sceneMode === '2D') {
          this.configure2DControls();
        } else {
          this.configure3DControls();
        }

        this.setupControlsChangeListener();
      }

      const activeCamera = this.sceneMode === '2D' ? this.orthographicCamera : this.perspectiveCamera;
      activeCamera.position.set(state.position.x, state.position.y, state.position.z);

      if (this.sceneMode === '3D') {
        this.controls.target.copy(DEFAULT_3D_TARGET);
      } else {
        // Enforce up vector and straight-down view for 2D
        activeCamera.up.set(0, 0, -1);
        this.controls.target.set(state.target.x, state.target.y, state.target.z);
      }

      this.controls.update();
      return true;
    } catch (e) {
      console.warn('Failed to set camera state:', e);
      return false;
    }
  }

  saveCameraState() {
    // Read-only embeds must not persist their orbit position to the host's storage.
    if (this.app?.isEmbedMode) return;
    try {
      const state = { ...this.getCameraState(), version: FRAME_VERSION, timestamp: Date.now() };
      localStorage.setItem(CAMERA_STATE_KEY, JSON.stringify(state));
    } catch (e) {
      console.warn('Failed to save camera state:', e);
    }
  }

  loadCameraState() {
    try {
      // Embeds always use the fixed pulled-back default framing — ignore any saved camera.
      if (this.app?.isEmbedMode) return false;
      const state = this.getSavedState();
      if (!state || state.sceneMode !== this.sceneMode) return false;

      const activeCamera = this.sceneMode === '2D' ? this.orthographicCamera : this.perspectiveCamera;
      activeCamera.position.set(state.position.x, state.position.y, state.position.z);

      if (this.sceneMode === '3D') {
        this.controls.target.copy(DEFAULT_3D_TARGET);
      } else {
        // Enforce up vector for 2D
        activeCamera.up.set(0, 0, -1);
        this.controls.target.set(state.target.x, state.target.y, state.target.z);
      }

      this.controls.update();
      return true;
    } catch (e) {
      console.warn('Failed to load camera state:', e);
      return false;
    }
  }

  clearCameraState() {
    try {
      localStorage.removeItem(CAMERA_STATE_KEY);
    } catch (e) {
      console.warn('Failed to clear camera state:', e);
    }
  }
}
